import React, { createContext, useCallback, useContext, useState } from "react";
import { useLoginStatus } from "./LoginStatusContext";
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useCustomNavigate } from "../hooks/useCustomNavigate";

// パスと対応する設定のマッピング
const PATH_FEATURE_MAP = {
  // Tenak AI
  "/mind-map": "tenak_ai.mind_map_enabled",
  "/chat": "tenak_ai.browsing_enabled",
  "/color-generator": "tenak_ai.color_palette_enabled",
  "/template": "tenak_ai.prompt_template_enabled",
  "/writing-ai": "tenak_ai.writing_ai_enabled",

  // Studio Juno
  "/juno/image-generator": "studio_juno.image_generation_enabled",
  "/juno/video-generator": "studio_juno.video_generation_enabled",
};

// パスをチェックする際のワイルドカード対応
const PATH_WILDCARDS = {
  "/chat/": "tenak_ai.browsing_enabled",
  "/writing-ai/": "tenak_ai.writing_ai_enabled",
  "/juno/sounds/": "studio_juno.sound_generation_enabled",
};

interface FeatureRestrictionContextType {
  isFeatureRestricted: (path: string) => boolean;
  showRestrictionModal: (path: string, lastAccessiblePath?: string) => void;
  restrictionModalOpen: boolean;
  closeRestrictionModal: () => void;
  redirectPath: string;
}

const FeatureRestrictionContext = createContext<FeatureRestrictionContextType | undefined>(undefined);

export const FeatureRestrictionProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { t } = useTranslation();
  const { user } = useLoginStatus();
  const navigate = useCustomNavigate();

  const [restrictionModalOpen, setRestrictionModalOpen] = useState(false);
  const [restrictedFeatureName, setRestrictedFeatureName] = useState("");
  const [redirectPath, setRedirectPath] = useState("");

  // パスに対応する設定値を取得
  const getSettingValueForPath = useCallback(
    (path: string) => {
      // エンタープライズユーザーでない場合は制限なし
      if (!user?.enterprise) return true;

      // 完全一致のパスをチェック
      if (PATH_FEATURE_MAP[path]) {
        const [service, feature] = PATH_FEATURE_MAP[path].split(".");
        return user.enterprise.organization.settings?.[service]?.[feature] ?? true;
      }

      // ワイルドカードパスをチェック
      for (const wildcardPath in PATH_WILDCARDS) {
        if (path.startsWith(wildcardPath)) {
          const [service, feature] = PATH_WILDCARDS[wildcardPath].split(".");
          return user.enterprise.organization.settings?.[service]?.[feature] ?? true;
        }
      }

      // マッピングがない場合は制限なし
      return true;
    },
    [user]
  );

  // パス名から表示名を取得
  const getFeatureNameFromPath = (path: string): string => {
    if (path.includes("mind-map")) return t("preferences.features.mindMap");
    if (path.includes("chat")) return t("preferences.features.browsing");
    if (path.includes("color-generator")) return t("preferences.features.colorPalette");
    if (path.includes("template")) return t("preferences.features.promptTemplate");
    if (path.includes("writing-ai")) return t("preferences.features.writingAI");
    if (path.includes("image-generator")) return t("preferences.features.imageGeneration");
    if (path.includes("video-generator")) return t("preferences.features.videoGeneration");
    if (path.includes("sounds")) return t("preferences.features.soundGeneration");
    return t("preferences.features.unknownFeature");
  };

  // 機能が制限されているかをチェック
  const isFeatureRestricted = useCallback(
    (path: string): boolean => {
      return !getSettingValueForPath(path);
    },
    [getSettingValueForPath]
  );

  // 制限モーダルを表示
  const showRestrictionModal = useCallback((path: string, lastAccessiblePath?: string) => {
    setRestrictedFeatureName(getFeatureNameFromPath(path));
    // 直前のアクセス可能だったパスがあればそれを使用し、なければホームに戻る
    setRedirectPath(lastAccessiblePath || "/");
    setRestrictionModalOpen(true);
  }, []);

  // 制限モーダルを閉じる
  const closeRestrictionModal = useCallback(() => {
    setRestrictionModalOpen(false);
    navigate(redirectPath);
  }, [navigate, redirectPath]);

  const value = {
    isFeatureRestricted,
    showRestrictionModal,
    restrictionModalOpen,
    closeRestrictionModal,
    redirectPath,
  };

  return (
    <FeatureRestrictionContext.Provider value={value}>
      {children}
      <RestrictionModal
        open={restrictionModalOpen}
        onClose={closeRestrictionModal}
        featureName={restrictedFeatureName}
      />
    </FeatureRestrictionContext.Provider>
  );
};

interface RestrictionModalProps {
  open: boolean;
  onClose: () => void;
  featureName: string;
}

const RestrictionModal: React.FC<RestrictionModalProps> = ({ open, onClose, featureName }) => {
  const { t } = useTranslation();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      BackdropProps={{
        style: {
          backdropFilter: "blur(5px)",
          backgroundColor: "rgba(0, 0, 0, 0.5)",
        },
      }}
    >
      <DialogTitle>{t("preferences.restriction.title")}</DialogTitle>
      <DialogContent>
        <Box sx={{ my: 1 }}>
          <Typography>{t("preferences.restriction.message", { feature: featureName })}</Typography>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary">
          {t("common.close")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export const useFeatureRestriction = () => {
  const context = useContext(FeatureRestrictionContext);
  if (!context) {
    throw new Error("useFeatureRestriction must be used within a FeatureRestrictionProvider");
  }
  return context;
};
